import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';
import './SchedulerDashboard.css';

interface ScheduledJob {
  config_id: string;
  job_name: string;
  job_type: string;
  cron_schedule: string;
  is_enabled: boolean;
  configuration: any;
  description: string;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_duration_ms: number | null;
  last_run_error: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
  is_running?: boolean;
}

interface ExecutionLog {
  log_id: string;
  job_name: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  notifications_sent: number;
  notifications_failed: number;
  error_message: string | null;
  execution_time_ms: number | null;
  metadata: any;
}

interface SchedulerStats {
  jobs: { total: number; enabled: number };
  executions: {
    total: number;
    successful: number;
    failed: number;
    total_notifications_sent: number;
    total_notifications_failed: number;
    avg_execution_time_ms: number;
  };
  recent_executions: Array<{
    job_name: string;
    started_at: string;
    status: string;
    notifications_sent: number;
    execution_time_ms: number;
  }>;
}

export const SchedulerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [stats, setStats] = useState<SchedulerStats | null>(null);
  const [selectedJob, setSelectedJob] = useState<ScheduledJob | null>(null);
  const [executionLogs, setExecutionLogs] = useState<ExecutionLog[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedJob) {
      fetchJobLogs(selectedJob.job_name);
    }
  }, [selectedJob]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/scheduler/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success && result.data) {
        setJobs(result.data.jobs);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load scheduled jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/scheduler/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (err: any) {
      console.error('Failed to load statistics:', err);
    }
  };

  const fetchJobLogs = async (jobName: string) => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/scheduler/jobs/${jobName}/logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success && result.data) {
        setExecutionLogs(result.data.logs);
      }
    } catch (err: any) {
      console.error('Failed to load job logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const handlePauseJob = async (jobName: string) => {
    try {
      setActionLoading(`pause-${jobName}`);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/scheduler/jobs/${jobName}/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success) {
        setSuccessMessage(`Job ${jobName} paused successfully`);
        fetchJobs();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to pause job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to pause job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeJob = async (jobName: string) => {
    try {
      setActionLoading(`resume-${jobName}`);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/scheduler/jobs/${jobName}/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success) {
        setSuccessMessage(`Job ${jobName} resumed successfully`);
        fetchJobs();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to resume job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resume job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTriggerJob = async (jobName: string) => {
    if (!window.confirm(`Manually trigger ${jobName}? This will send notifications immediately.`)) {
      return;
    }

    try {
      setActionLoading(`trigger-${jobName}`);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/scheduler/jobs/${jobName}/trigger`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success) {
        const notificationsSent = result.data.notifications_sent || 0;
        setSuccessMessage(`Job ${jobName} triggered successfully. ${notificationsSent} notifications sent.`);
        fetchJobs();
        fetchStats();
        if (selectedJob?.job_name === jobName) {
          fetchJobLogs(jobName);
        }
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(result.message || 'Failed to trigger job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to trigger job');
    } finally {
      setActionLoading(null);
    }
  };

  const getJobIcon = (jobType: string) => {
    switch (jobType) {
      case 'payment_reminder':
        return '📅';
      case 'overdue_alert':
        return '⚠️';
      default:
        return '⚙️';
    }
  };

  const getStatusBadgeClass = (status: string | null) => {
    switch (status) {
      case 'success':
        return 'status-success';
      case 'failed':
        return 'status-failed';
      case 'running':
        return 'status-running';
      default:
        return 'status-unknown';
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="scheduler-dashboard">
      <div className="page-header">
        <div className="header-content">
          <h1>📊 Scheduler Dashboard</h1>
          <p className="header-subtitle">Manage scheduled jobs and automated notifications</p>
        </div>
        <Button onClick={() => navigate('/admin/dashboard')} variant="secondary">
          Back to Dashboard
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>✓ {successMessage}</span>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <h3>Total Jobs</h3>
              <p className="stat-value">{stats.jobs.total}</p>
              <p className="stat-sub">{stats.jobs.enabled} enabled</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>Successful (7d)</h3>
              <p className="stat-value">{stats.executions.successful}</p>
              <p className="stat-sub">of {stats.executions.total} executions</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📧</div>
            <div className="stat-content">
              <h3>Notifications</h3>
              <p className="stat-value">{stats.executions.total_notifications_sent}</p>
              <p className="stat-sub">{stats.executions.total_notifications_failed} failed</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-content">
              <h3>Avg Duration</h3>
              <p className="stat-value">{formatDuration(stats.executions.avg_execution_time_ms)}</p>
              <p className="stat-sub">per execution</p>
            </div>
          </div>
        </div>
      )}

      {/* Jobs List */}
      <div className="scheduler-content">
        <div className="jobs-section">
          <h2>Scheduled Jobs</h2>
          <div className="jobs-grid">
            {jobs.map((job) => (
              <div
                key={job.config_id}
                className={`job-card ${selectedJob?.config_id === job.config_id ? 'selected' : ''} ${!job.is_enabled ? 'disabled' : ''}`}
                onClick={() => setSelectedJob(job)}
              >
                <div className="job-header">
                  <div className="job-title">
                    <span className="job-icon">{getJobIcon(job.job_type)}</span>
                    <h3>{job.job_name}</h3>
                  </div>
                  {job.is_enabled && <span className="status-badge enabled">ENABLED</span>}
                  {!job.is_enabled && <span className="status-badge disabled">PAUSED</span>}
                </div>
                <p className="job-description">{job.description}</p>
                <div className="job-meta">
                  <div className="meta-item">
                    <span className="meta-label">Schedule:</span>
                    <span className="meta-value">{job.cron_schedule}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Last Run:</span>
                    <span className="meta-value">{formatDateTime(job.last_run_at)}</span>
                  </div>
                  {job.last_run_status && (
                    <div className="meta-item">
                      <span className="meta-label">Status:</span>
                      <span className={`status-badge-sm ${getStatusBadgeClass(job.last_run_status)}`}>
                        {job.last_run_status}
                      </span>
                    </div>
                  )}
                  {job.is_enabled && job.next_run_at && (
                    <div className="meta-item">
                      <span className="meta-label">Next Run:</span>
                      <span className="meta-value">{formatDateTime(job.next_run_at)}</span>
                    </div>
                  )}
                </div>
                <div className="job-actions">
                  {job.is_enabled ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePauseJob(job.job_name);
                      }}
                      loading={actionLoading === `pause-${job.job_name}`}
                      disabled={!!actionLoading}
                    >
                      Pause
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResumeJob(job.job_name);
                      }}
                      loading={actionLoading === `resume-${job.job_name}`}
                      disabled={!!actionLoading}
                    >
                      Resume
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTriggerJob(job.job_name);
                    }}
                    loading={actionLoading === `trigger-${job.job_name}`}
                    disabled={!!actionLoading}
                  >
                    Trigger Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Execution Logs */}
        {selectedJob && (
          <div className="logs-section">
            <div className="logs-header">
              <h2>Execution Logs - {selectedJob.job_name}</h2>
              <Button variant="secondary" size="sm" onClick={() => setSelectedJob(null)}>
                Close
              </Button>
            </div>

            {logsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="logs-container">
                {executionLogs.length === 0 ? (
                  <div className="empty-logs">
                    <p>No execution history yet</p>
                  </div>
                ) : (
                  <div className="logs-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Started</th>
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Notifications</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {executionLogs.map((log) => (
                          <tr key={log.log_id}>
                            <td>{formatDateTime(log.started_at)}</td>
                            <td>
                              <span className={`status-badge-sm ${getStatusBadgeClass(log.status)}`}>
                                {log.status}
                              </span>
                            </td>
                            <td>{formatDuration(log.execution_time_ms)}</td>
                            <td>
                              <span className="notifications-count">
                                {log.notifications_sent} sent
                                {log.notifications_failed > 0 && (
                                  <span className="failed-count"> / {log.notifications_failed} failed</span>
                                )}
                              </span>
                            </td>
                            <td>
                              {log.error_message ? (
                                <span className="error-text" title={log.error_message}>
                                  {log.error_message.substring(0, 50)}
                                  {log.error_message.length > 50 && '...'}
                                </span>
                              ) : (
                                <span className="no-error">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {selectedJob.last_run_error && (
              <div className="error-details">
                <h4>Last Error Details</h4>
                <pre>{selectedJob.last_run_error}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
